import { useEffect, useState, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mail, Phone, MapPin, Search, Users, Pencil, Trash2, Upload as UploadIcon } from "lucide-react";
import CustomersTabs from "@/components/CustomersTabs";
import StateDistrictFilter from "@/components/StateDistrictFilter";
import EditUnifiedContactDialog from "@/components/EditUnifiedContactDialog";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const KIND_LABEL = {
  contact: "Contact",
  lead: "Lead",
  marketing: "Cold",
  client_primary: "Client",
};
const KIND_CLASS = {
  contact: "bg-blue-50 text-blue-800 border-blue-200",
  lead: "bg-amber-50 text-amber-800 border-amber-200",
  marketing: "bg-slate-50 text-slate-800 border-slate-200",
  client_primary: "bg-primary-50 text-primary-700 border-primary-200",
};

const KIND_TABS = [
  { v: "",                  l: "All" },
  { v: "contact",           l: "Contacts" },
  { v: "lead",              l: "Leads" },

  { v: "client_primary",    l: "Clients" },
];

// Header aliases → CRM contact field names for the Upload Contacts flow.
const CRM_HEADER_MAP = {
  name: "name", "full name": "name", "contact name": "name", "contact person": "name", contact: "name",
  designation: "designation", title: "designation", role: "designation",
  company: "company_name", "company name": "company_name", company_name: "company_name", organisation: "company_name", organization: "company_name",
  email: "email", "e-mail": "email", "email id": "email", mail: "email",
  phone: "phone", mobile: "phone", "phone number": "phone", "mobile number": "phone", "contact no": "phone",
  state: "state", city: "district", district: "district",
  notes: "notes", remarks: "notes",
};

function UploadContactsDialog({ open, onOpenChange, onDone }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [running, setRunning] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { if (open) { setRows([]); setFileName(""); } }, [open]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const parsed = [];
      for (const raw of json) {
        const row = {};
        Object.entries(raw).forEach(([k, v]) => {
          const key = CRM_HEADER_MAP[String(k).trim().toLowerCase()];
          const val = String(v ?? "").trim();
          if (key && val) row[key] = val;
        });
        if (row.name || row.email || row.phone) parsed.push(row);
      }
      if (parsed.length === 0) { toast.error("No valid rows found. Needs columns like: name, email, phone, company"); return; }
      setRows(parsed);
      setFileName(file.name);
    } catch {
      toast.error("Could not read the file — use .xlsx, .xls or .csv");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const uploadNow = async () => {
    if (rows.length === 0) { toast.error("Upload a file first"); return; }
    setRunning(true);
    try {
      let inserted = 0, skipped = 0;
      for (let i = 0; i < rows.length; i += 2000) {
        const { data } = await api.post("/contacts/bulk-import", { contacts: rows.slice(i, i + 2000) });
        inserted += data.inserted; skipped += data.skipped;
      }
      toast.success(`Uploaded ${inserted} CRM contact${inserted === 1 ? "" : "s"} · ${skipped} skipped (duplicates)`);
      onOpenChange(false);
      onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setRunning(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><UploadIcon className="h-5 w-5 text-primary" /> Upload CRM contacts</DialogTitle>
          <DialogDescription>
            Excel (.xlsx) or CSV. Contacts are added ONLY to CRM Contacts — completely separate from Marketing Contacts.
            Recognised columns: name, designation, company, email, phone, state, city, notes.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs font-semibold">File</Label>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFile}
            className="mt-1 block w-full text-sm file:mr-3 file:rounded-xl file:border-0 file:bg-primary/10 file:px-4 file:py-2 file:text-primary file:font-semibold file:cursor-pointer cursor-pointer rounded-2xl border border-border/60 p-1.5"
            data-testid="crm-upload-file" />
          {rows.length > 0 && (
            <div className="mt-2 rounded-2xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800" data-testid="crm-upload-preview">
              <strong>{rows.length}</strong> contact{rows.length === 1 ? "" : "s"} ready from <em>{fileName}</em>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-2xl tap" onClick={uploadNow} disabled={running || rows.length === 0} data-testid="crm-upload-run">
            {running ? "Uploading…" : <><UploadIcon className="h-4 w-4 mr-1.5" /> Upload {rows.length > 0 ? rows.length : ""}</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AllContacts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canUpload = isAdmin || (user?.permissions || []).includes("add_contacts");
  const [openUpload, setOpenUpload] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  // Inline edit state — clicking Edit on any row opens the unified edit
  // dialog with the row pre-filled. Refreshes the list after save.
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/all-contacts", {
        params: {
          q: q || undefined,
          source_kind: kind || undefined,
          state: stateFilter || undefined,
          district: districtFilter || undefined,
        },
      });
      setRows(data.contacts || []);
    } finally { setLoading(false); }
  }, [q, kind, stateFilter, districtFilter]);

  useEffect(() => { load(); }, [load]);

  const deleteRow = async (r) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    try {
      await api.delete(`/all-contacts/${r.kind}/${r.id}`);
      toast.success("Contact deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete contact");
    }
  };

  return (
    <div className="px-5 sm:px-8 lg:px-10 py-5 max-w-6xl mx-auto">
      <CustomersTabs />
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Unified roll-up</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1" data-testid="all-contacts-title">
            <Users className="inline-block h-7 w-7 mr-2 text-primary" />All contacts
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Every person in the CRM — decision makers, leads, cold prospects, and primary client contacts — in one searchable list.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canUpload && (
            <Button variant="outline" className="rounded-2xl tap" onClick={() => setOpenUpload(true)} data-testid="crm-upload-btn">
              <UploadIcon className="h-4 w-4 mr-1.5" /> Upload Contacts
            </Button>
          )}
          <Badge variant="outline" className="rounded-2xl px-3 py-1" data-testid="all-contacts-count">
            {loading ? "…" : `${rows.length} people`}
          </Badge>
        </div>
      </header>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, company, email, phone…"
            className="rounded-2xl h-11 pl-9"
            data-testid="all-contacts-search"
          />
        </div>
        <StateDistrictFilter
          state={stateFilter}
          district={districtFilter}
          onChange={({ state, district }) => { setStateFilter(state); setDistrictFilter(district); }}
          testId="all-contacts-filter"
        />
      </div>

      {/* Kind segmented control */}
      <div className="inline-flex p-1 rounded-2xl bg-muted/50 border border-border/60 gap-1 mb-5 flex-wrap" data-testid="all-contacts-kind-tabs">
        {KIND_TABS.map((opt) => (
          <button
            key={opt.v || "all"}
            type="button"
            onClick={() => setKind(opt.v)}
            data-testid={`all-contacts-kind-${opt.v || "all"}`}
            className={`px-3 h-8 rounded-xl text-xs font-semibold tap transition ${
              kind === opt.v
                ? "bg-card text-foreground shadow-sm border border-border/60"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => (<Skeleton key={i} className="h-16 w-full rounded-2xl" />))}</div>
      ) : rows.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-border/60">
          <CardContent className="p-10 text-center text-muted-foreground">
            <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-semibold">No contacts match these filters yet.</p>
            <p className="text-xs mt-1">Adjust the search or filters above, or add a new lead / contact from the other tabs.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="all-contacts-list">
          {rows.map((r) => {
            const loc = [r.district, r.state].filter(Boolean).join(", ");
            const sub = [r.designation, r.company_name].filter(Boolean).join(" · ");
            return (
              <Card key={`${r.kind}-${r.id}`} className="rounded-2xl border border-border/60 shadow-soft tap" data-testid={`all-contact-row-${r.kind}-${r.id}`}>
                <CardContent className="p-3 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold truncate">{r.name || "—"}</p>
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase tracking-wider ${KIND_CLASS[r.kind] || ""}`}
                      >
                        {KIND_LABEL[r.kind] || r.kind}
                      </Badge>
                      {(r.also_in || []).map((k) => (
                        <Badge
                          key={k}
                          variant="outline"
                          className={`text-[10px] uppercase tracking-wider opacity-70 ${KIND_CLASS[k] || ""}`}
                          data-testid={`all-contact-also-in-${k}`}
                        >
                          {KIND_LABEL[k] || k}
                        </Badge>
                      ))}
                    </div>
                    {sub && <p className="text-xs text-muted-foreground truncate mt-0.5">{sub}</p>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                    {r.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{r.email}</span>}
                    {r.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{r.phone}</span>}
                    {loc && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{loc}</span>}
                    {(r.created_by_name || r.owner_name) && (
                      <span data-testid={`all-contact-created-by-${r.kind}-${r.id}`}>
                        Created by: <strong className="text-foreground/70">{r.created_by_name || r.owner_name}</strong>
                      </span>
                    )}
                    {r.last_activity && <span className="opacity-60">{fmtDate(r.last_activity)}</span>}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-2xl tap shrink-0"
                    onClick={() => setEditRow(r)}
                    data-testid={`all-contact-edit-${r.kind}-${r.id}`}
                  >
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-2xl tap shrink-0 text-rose-600 hover:text-rose-700 border-rose-200 hover:border-rose-300"
                      onClick={() => deleteRow(r)}
                      data-testid={`all-contact-delete-${r.kind}-${r.id}`}
                    >
                      <Trash2 className="h-3 w-3 mr-1" /> Delete
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <EditUnifiedContactDialog
        open={!!editRow}
        onOpenChange={(v) => { if (!v) setEditRow(null); }}
        row={editRow}
        onSaved={load}
      />
      <UploadContactsDialog open={openUpload} onOpenChange={setOpenUpload} onDone={load} />
    </div>
  );
}
